import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { t } from '../i18n.js';

/**
 * Platformbeleid bij een FMC, op de tab Syslog (docs/besluiten/beleid-op-de-fmc.md): een nieuw platformbeleid
 * aanmaken, met de syslog-standaard en de syslogserver van een gekozen firewall, en het aan een firewall onder
 * deze FMC toewijzen. Het toewijzen is een uitrol van die firewall; de stappen staan daar, op zijn tab Syslog.
 */
export default function PlatformFmc({ bron, mij }) {
  const magUitrollen = mij.permissions.includes('bronnen.uitrollen');
  const client = useQueryClient();
  const [vraag, zetVraag] = useState(false);
  const q = useQuery({ queryKey: ['fmc-beleid', bron.id], queryFn: () => api.fmcBeleid(bron.id), enabled: vraag, retry: false });
  const d = q.data?.data;
  const [nieuw, zetNieuw] = useState({ naam: '', firewall: '' });
  const [toewijs, zetToewijs] = useState(null);
  const [melding, zetMelding] = useState(null);
  const [bezig, zetBezig] = useState(false);

  const doe = async (fn) => {
    if (bezig) return;
    zetMelding(null); zetBezig(true);
    try { zetMelding({ goed: true, ...(await fn()) }); await q.refetch(); }
    catch (e) { zetMelding({ goed: false, tekst: t(e.details ? Object.values(e.details)[0] : e.message) }); }
    finally { zetBezig(false); }
  };
  const fw = (id) => d?.firewalls.find((x) => x.id === Number(id));
  const doelFw = toewijs && fw(toewijs.firewall);

  const maak = () => doe(async () => {
    const r = (await api.maakFmcBeleid(bron.id, { soort: 'platform', naam: nieuw.naam.trim(), firewall: nieuw.firewall ? Number(nieuw.firewall) : null })).data;
    zetNieuw({ naam: '', firewall: '' });
    /* Meteen door naar toewijzen, aan de firewall waarvoor hij gemaakt is. */
    zetToewijs({ beleid: { id: r.id, name: r.name, voor: r.firewall?.id ?? null }, firewall: r.firewall ? String(r.firewall.id) : '', bevestig: '', reden: '', onderbreking: false });
    return { tekst: r.firewall ? t('{naam} aangemaakt, met de syslog-standaard en de syslogserver van {fw}.', { naam: r.name, fw: r.firewall.name })
      : t('{naam} aangemaakt, met de syslog-standaard (zonder syslogserver).', { naam: r.name }) };
  });
  const wijsToe = () => doe(async () => {
    const f = doelFw;
    const r = (await api.wijsBeleidToe(f.id, { platformId: toewijs.beleid.id, bevestig: toewijs.bevestig.trim(), onderbrekingMag: toewijs.onderbreking, reden: toewijs.reden.trim() || null })).data;
    zetToewijs(null);
    await client.invalidateQueries({ queryKey: ['bron'] });
    return { tekst: t('Toewijzen {n} gestart voor {fw}.', { n: r.id, fw: f.name }), fw: f };
  });

  const huidig = (f) => d?.platform.find((p) => p.devices.some((x) => x.name === f.name));

  return (
    <section className="card">
      <header><h2>{t('Platformbeleid')}</h2>
        <span className="meta"><button type="button" className="namelink" disabled={q.isFetching} onClick={() => (vraag ? q.refetch() : zetVraag(true))}>
          {q.isFetching ? t('Bezig met lezen van de FMC…') : vraag ? t('Opnieuw lezen') : t('Lezen van de FMC')}</button></span></header>
      {!vraag && <p className="soft">{t('Een nieuw platformbeleid aanmaken met de syslog-standaard, en het toewijzen aan een firewall onder deze FMC.')}</p>}
      {melding && <div className={`melder ${melding.goed ? 'blauw' : 'rood'}`} style={{ marginBottom: 10 }}>{melding.tekst}
        {melding.fw && <> <Link to={`/bronnen/${melding.fw.id}?tab=syslog`}>{t('Volg de uitrol bij {fw}', { fw: melding.fw.name })}</Link></>}</div>}
      {q.isError && <div className="melder rood">{t(q.error.message)}</div>}
      {d && !d.available && <p className="empty">{t(d.reason)}</p>}
      {d?.available && (
        <>
          <table>
            <thead><tr><th>{t('Naam')}</th><th>{t('Hangt aan')}</th>{magUitrollen && <th />}</tr></thead>
            <tbody>{d.platform.map((p) => (
              <tr key={p.id}>
                <td className="mono">{p.name}</td>
                <td>{p.devices.length ? p.devices.map((x) => `${x.name}${x.via ? ` (${t('via {groep}', { groep: x.via })})` : ''}`).join(', ') : <span className="muted">{t('geen firewall')}</span>}</td>
                {magUitrollen && <td style={{ textAlign: 'right' }}>{d.firewalls.length > 0 && toewijs?.beleid.id !== p.id && (
                  <button type="button" className="namelink" onClick={() => { zetMelding(null); zetToewijs({ beleid: { id: p.id, name: p.name, voor: null }, firewall: '', bevestig: '', reden: '', onderbreking: false }); }}>{t('Toewijzen…')}</button>)}</td>}
              </tr>
            ))}</tbody>
          </table>

          {magUitrollen && toewijs && (
            <div className="callout" style={{ marginTop: 14, display: 'grid', gap: 8 }}>
              <b>{t('{naam} toewijzen', { naam: toewijs.beleid.name })}</b>
              <label className="field"><span>{t('Aan firewall')}</span>
                <select value={toewijs.firewall} onChange={(e) => zetToewijs({ ...toewijs, firewall: e.target.value, bevestig: '' })}>
                  <option value="">{t('Kies…')}</option>
                  {d.firewalls.map((f) => <option key={f.id} value={f.id}>{f.name}{huidig(f) ? ` · ${t('nu {naam}', { naam: huidig(f).name })}` : ''}</option>)}</select></label>
              {doelFw && (
                <>
                  {huidig(doelFw)?.id === toewijs.beleid.id
                    ? <span className="soft">{t('{fw} heeft dit beleid al.', { fw: doelFw.name })}</span>
                    : (
                      <>
                        {/* Een beleid zonder de server van deze firewall: na de uitrol stuurt hij niets meer naar ZERO. */}
                        {toewijs.beleid.voor !== doelFw.id && <div className="callout warn">{t('Staat de syslogserver van {fw} niet in dit beleid, dan stuurt {fw} na de uitrol niets meer naar ADTS ZERO. Rol daarna bij {fw} de syslog-standaard uit, of maak hierboven een beleid voor {fw}.', { fw: doelFw.name })}</div>}
                        <span className="soft">{t('Het vorige platformbeleid van {fw} blijft op de FMC bestaan. Hing hij er via een groep aan, dan hangt hij hierna rechtstreeks aan dit beleid.', { fw: doelFw.name })}</span>
                        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input type="checkbox" checked={toewijs.onderbreking} onChange={(e) => zetToewijs({ ...toewijs, onderbreking: e.target.checked })} />
                          {t('De uitrol mag het verkeer even onderbreken (als de FMC dat meldt, bijvoorbeeld een herstart van Snort)')}</label>
                        {doelFw.status === 'actief' && <label className="field"><span>{t('Reden (deze firewall is in gebruik)')}</span>
                          <input className="input" value={toewijs.reden} onChange={(e) => zetToewijs({ ...toewijs, reden: e.target.value })} placeholder={t('Bijvoorbeeld het nummer van de change')} /></label>}
                        <label className="field"><span>{t('Typ {naam} om te bevestigen', { naam: doelFw.name })}</span>
                          <input className="input mono" value={toewijs.bevestig} onChange={(e) => zetToewijs({ ...toewijs, bevestig: e.target.value })} autoComplete="off" /></label>
                      </>
                    )}
                </>
              )}
              <div className="actions" style={{ paddingTop: 0 }}>
                <button type="button" className="btn danger" disabled={bezig || !doelFw || huidig(doelFw)?.id === toewijs.beleid.id || toewijs.bevestig.trim() !== doelFw.name
                  || (doelFw.status === 'actief' && toewijs.reden.trim().length < 10)} onClick={wijsToe}>{bezig ? t('Bezig…') : t('Toewijzen en uitrollen')}</button>
                <button type="button" className="btn" onClick={() => zetToewijs(null)}>{t('Annuleren')}</button>
              </div>
            </div>
          )}

          {magUitrollen && (
            <form className="form" style={{ marginTop: 14 }} onSubmit={(e) => { e.preventDefault(); maak(); }}>
              <h3 style={{ margin: 0 }}>{t('Nieuw platformbeleid')}</h3>
              <div className="two">
                <label className="field"><span>{t('Naam')}</span>
                  <input className="input mono" value={nieuw.naam} onChange={(e) => zetNieuw((x) => ({ ...x, naam: e.target.value }))} placeholder="ADTS-AMS-TDV012-PS" /></label>
                <label className="field"><span>{t('Voor firewall')}</span>
                  <select value={nieuw.firewall} onChange={(e) => zetNieuw((x) => ({ ...x, firewall: e.target.value }))}>
                    <option value="">{t('Geen (zonder syslogserver)')}</option>
                    {d.firewalls.map((f) => <option key={f.id} value={f.id} disabled={!f.route || !f.sensorIp}>{f.name}{!f.route ? ` · ${t('eerst de route kiezen')}` : ''}</option>)}</select></label>
              </div>
              {d.firewalls.some((f) => !f.route) && <small className="soft">{t('Bij een firewall zonder route: kies eerst op zijn tab Syslog hoe syslog naar ADTS ZERO gaat (management-interface of een data-zone).')}{' '}
                {d.firewalls.filter((f) => !f.route).map((f, i) => <span key={f.id}>{i ? ', ' : ''}<Link to={`/bronnen/${f.id}?tab=syslog`}>{f.name}</Link></span>)}</small>}
              <div className="actions"><button className="btn primary" disabled={bezig || !nieuw.naam.trim()}>{bezig ? t('Bezig…') : t('Aanmaken')}</button></div>
              <p className="noot">{t('ADTS ZERO zet de syslog-standaard in het nieuwe beleid; met een firewall gekozen ook zijn syslogserver, zodat hij na het toewijzen blijft sturen. Het beleid hangt nog nergens aan tot je het toewijst. Verwijderen kan in ADTS ZERO niet.')}</p>
            </form>
          )}
        </>
      )}
    </section>
  );
}
